import React from 'react'
import Dropzone from 'react-dropzone'
import TextareaAutosize from 'react-autosize-textarea'
import WarningModal from './warning-modal'
import { useMutation } from '@apollo/client'
import fetchReflection from '../queries/reflection'
import submitAnswer from '../mutations/submit-answer'
import updateAnswer from '../mutations/update-answer'
import attachEvidence from '../mutations/attach-evidence'
import debounce from 'debounce'
import { isPast } from 'date-fns'
import { formatDate } from '../utils'

const evidenceName = (evidence) => {
  if (!evidence) return null
  return typeof evidence === 'string'
    ? evidence.split('/').pop().replace(/\?.+/, '')
    : evidence.name
}

const wordCount = (text) => {
  if (!text) return 0
  return text.split(' ').filter((s) => /[A-z]+/.test(s)).length
}

function ReflectionForm(props) {
  const {
    data: { answer },
  } = props
  const [submitted, setSubmitted] = React.useState(false)
  const [warningMsg, setWarningMsg] = React.useState(null)
  const [isOpen, setIsOpen] = React.useState(false)
  const [warningAction, setWarningAction] = React.useState(null)
  const [errorMsg, setErrorMsg] = React.useState(null)
  const [saved, setSaved] = React.useState(false)
  const [text, setText] = React.useState(answer && answer.text)
  const [evidence, setEvidence] = React.useState(answer && answer.evidence)

  const [submitAnswerMutation] = useMutation(submitAnswer)
  const [updateAnswerMutation] = useMutation(updateAnswer)
  const [attachEvidenceMutation] = useMutation(attachEvidence)

  React.useEffect(() => {
    const {
      data: { answer },
    } = props
    const newText = text || (answer && answer.text)

    setSubmitted(false)
    setText(newText)
    setEvidence(answer && answer.evidence)
  }, [props, text, answer])

  const doUpdateAnswer = debounce(() => {
    const { data, onSave } = props

    updateAnswerMutation({
      variables: { assignmentId: data.id, text },
    }).then(() => {
      onSave()
    })
  }, 500)

  const handleDrop = (files) => {
    const { data, attachEvidence } = props
    const evidence = files[0]
    setEvidence(evidence)

    attachEvidenceMutation({
      variables: { assignmentId: data.id, evidence },
    })
  }

  const handleDropReject = () => {
    setWarningMsg(
      'Unable to attach file. Please make sure the file is a PDF 10MB or smaller.'
    )
    setIsOpen(true)
    setWarningAction(null)
  }

  const handleChange = (e) => {
    const wc = wordCount(e.target.value)
    if (wc > 450) return
    setText(e.target.value)
    doUpdateAnswer()
  }

  const doSubmit = () => {
    const { id, data, onSuccess, submitAnswer } = props

    submitAnswerMutation({
      refetchQueries: [
        {
          query: fetchReflection,
          variables: { id },
        },
      ],
      variables: {
        assignmentId: data.id,
      },
    }).then(({ data: { submitAnswer } }) => {
      if (submitAnswer) {
        setSubmitted(true)
        onSuccess()
      } else {
        // We fail to submit without triggering an exception
        setWarningMsg(
          'There was a problem submitting your answer. Please check your answer and try again.'
        )
        setIsOpen(true)
        setWarningAction(null)
      }
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const { data } = props
    const { requireEvidence } = data
    const wc = wordCount(text)

    if (requireEvidence && !evidence) {
      setErrorMsg('Evidence is required!')
      setTimeout(() => {
        setErrorMsg(null)
      }, 5000)
      return
    }

    if (wc === 0) {
      setWarningMsg('Please respond before submitting.')
      setIsOpen(true)
      setWarningAction(null)
      return
    }

    if (wc < 80) {
      setWarningMsg('Self-check! The minimum word count is 80.')
      setIsOpen(true)
      setWarningAction(null)
      return
    }

    doSubmit()
  }

  const dismissWarning = (e) => {
    e.preventDefault()

    setWarningMsg(null)
    setIsOpen(false)
  }

  const renderEvidence = () => {
    return (
      <div className="attachments">
        <Dropzone
          className="attach-evidence"
          onDrop={handleDrop}
          onDropRejected={handleDropReject}
          maxSize={10000000}
          style={{}}
        >
          <i className="material-icons">cloud_upload</i>
          {evidence ? (
            <span>{evidenceName(evidence)}</span>
          ) : (
            <div>
              <span>Attach Evidence (PDF).</span>
              &nbsp;Size limit: 10MB.
            </div>
          )}
        </Dropzone>
      </div>
    )
  }
  const { active, data, handleFocus } = props
  const { id, dueAt, metaReflection, question, requireEvidence } = data
  const formClasses = active ? 'reflection-form active' : 'reflection-form'
  const dueAtClasses = isPast(dueAt) ? 'due-at late' : 'due-at'
  const msgClasses = `msg ${saved && 'saved'} ${errorMsg && 'error'}`
  const formId = `assignment-${id}`

  return (
    <form id={formId} className={formClasses} onSubmit={handleSubmit}>
      <div className="reflection-card">
        {metaReflection && (
          <label className="meta-reflection">Meta-Reflection</label>
        )}
        {dueAt && (
          <div className={dueAtClasses}>
            <i className="material-icons">access_time</i>
            Due {formatDate(dueAt, "EE, MMMM d 'at' h:mma")}
          </div>
        )}
        <label>{question.text}</label>
        <TextareaAutosize
          value={text || ''}
          placeholder="+  Add response"
          onChange={handleChange}
          onFocus={() => handleFocus(id)}
        />
        <div className="word-count">Words: {wordCount(text)} / 450</div>
      </div>
      {requireEvidence && renderEvidence()}
      <div className="actions">
        <button>
          {submitted ? <i className="material-icons">check</i> : 'Submit'}
        </button>
        <span className={msgClasses}>
          {saved && 'Draft saved!'}
          {errorMsg}
        </span>
      </div>
      <WarningModal
        isOpen={isOpen}
        onClose={dismissWarning}
        onConfirm={warningAction}
      >
        <h2 style={{ marginTop: 48, marginBottom: 54 }}>{warningMsg}</h2>
      </WarningModal>
    </form>
  )
}

export default ReflectionForm
